import { linkVariants } from '@heroui/react';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode, AnchorHTMLAttributes, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { useIframeModal } from '@/Providers/IframeModalProvider';
import { usePreserveSearchParams } from '@/hooks/use-navigate-with-preserved-search-params';
import usePlatform from '@/hooks/use-platform';
import DynamicT from '@/shared/components/DynamicT';

const typeClass = {
  primary: 'text-sm',
  secondary: 'font-normal text-inherit hover:text-foreground',
} as const;

export type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  readonly className?: string;
  readonly children?: ReactNode;
  readonly text?: TFuncKey;
  readonly icon?: ReactNode;
  readonly type?: 'primary' | 'secondary';
  /** A link inside running text, whose hit area must not spill onto the lines around it. */
  readonly isInline?: boolean;
} & Partial<LinkProps>;

// Standalone links render at the text's 20px line height, under the 24px minimum target; the
// pseudo-element extends the hit area to 44px without moving the layout. The cancelled inline
// padding keeps the focus ring off the first and last glyphs.
const hitAreaClass =
  "relative -mx-1 px-1 after:absolute after:-inset-x-1 after:-inset-y-3 after:content-[''] [-webkit-touch-callout:none]";

// Router-only props that must not leak onto a plain <a>.
const omitRouterProps = ({
  replace,
  state,
  reloadDocument,
  preventScrollReset,
  relative,
  viewTransition,
  ...anchorProps
}: Omit<Props, 'to'>) => anchorProps;

const activateOnKey = (event: KeyboardEvent<HTMLAnchorElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.currentTarget.click();
  }
};

// Integration tests and tenant CSS select these as `a`, so every variant stays an anchor —
// including the action-only ones, which get button semantics instead of a link role.
const TextLink = ({
  className,
  children,
  text,
  icon,
  type = 'primary',
  isInline = false,
  to,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const { getTo } = usePreserveSearchParams();
  const { isMobile } = usePlatform();
  const { setModalState } = useIframeModal();

  // WKWebView and Android WebView can't open a new window, so `_blank` links open in an
  // iframe modal on mobile instead.
  const getHyperLinkProps = () => {
    const { href, target, onClick, ...others } = omitRouterProps(rest);

    if (!href || target !== '_blank') {
      return { href, target, onClick, ...others };
    }

    return {
      href,
      target,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isMobile) {
          const title = text && t(text);
          event.preventDefault();
          setModalState({ href, title: typeof title === 'string' ? title : undefined });
        }

        onClick?.(event);
      },
      ...others,
    };
  };

  const linkClassName = linkVariants().base({
    className: classNames(
      typeClass[type],
      Boolean(icon) && 'gap-1.5 [&>svg]:size-4',
      !isInline && hitAreaClass,
      className
    ),
  });

  if (to) {
    return (
      <Link className={linkClassName} to={getTo(to)} {...rest}>
        {icon}
        {children ?? <DynamicT forKey={text} />}
      </Link>
    );
  }

  const hyperLinkProps = getHyperLinkProps();
  const isAction = !hyperLinkProps.href;

  return (
    <a
      className={linkClassName}
      {...(isAction && { role: 'button', tabIndex: 0, onKeyDown: activateOnKey })}
      {...hyperLinkProps}
      rel="noopener"
    >
      {icon}
      {children ?? <DynamicT forKey={text} />}
    </a>
  );
};

export default TextLink;
